import { InputTypes } from '@enums/input-types';
import { LogicalOperators } from '@enums/logical-operators';
import { ExternalData } from '@models/dynamic-forms/external-data';
import { QuestionHint } from '@models/dynamic-forms/question-hint';
import { QuestionValidator } from '@models/dynamic-forms/question-validator';
import { QuestionVisibility } from '@models/dynamic-forms/question-visibility';
import { QuestionWarning } from '@models/dynamic-forms/question-warning';
import { Metadata } from '@models/metadata';
import { SelectableOption } from '@models/selectable-option';

export class Question {
  public id: string;
  public type: InputTypes;
  public value: any;
  public label: string;
  public extraStorageKey: string;
  public validators: QuestionValidator[];
  public options: SelectableOption[];
  public visibility: QuestionVisibility;
  public warnings: QuestionWarning[];
  public hint: QuestionHint;
  public placeholder: string;
  public metadata: Metadata;
  public htmlDescription: string;
  public forceOptionWrapping: boolean;

  public constructor(
    options: {
      id: string;
      type: InputTypes;
      value?: any;
      label?: string;
      extraStorageKey?: string;
      validators?: QuestionValidator[];
      options?: SelectableOption[];
      visibility?: QuestionVisibility;
      warnings?: QuestionWarning[];
      hint?: QuestionHint;
      placeholder?: string;
      metadata?: Metadata;
      htmlDescription?: string;
      forceOptionWrapping?: boolean;
    },
    externalData?: ExternalData,
  ) {
    this.id = options.id;
    this.type = options.type;
    this.value = options.value;
    this.label = options.label ? this.replacePlaceholders(options.label, externalData || {}) : '';
    this.extraStorageKey = options.extraStorageKey || '';
    this.validators = options.validators || [];
    this.setSelectableOptions(options.options);
    this.setVisibility(options.visibility);
    this.warnings = options.warnings ? this.processWarnings(options.warnings, externalData || {}) : [];
    this.hint = options.hint?.message ? this.processHint(options.hint, externalData || {}) : null;
    this.placeholder = options.placeholder || '';
    this.metadata = options.metadata || {};
    this.htmlDescription = options.htmlDescription
      ? this.replacePlaceholders(options.htmlDescription, externalData || {})
      : '';
    this.forceOptionWrapping = options?.forceOptionWrapping || false;
  }

  /**
   * Sets the options for the question, ensuring that each option has a label.
   * If an option does not have a label, it will be set to the value of the option.
   *
   * @param {SelectableOption[]} options The options to set for the question.
   */
  private setSelectableOptions(options: SelectableOption[] = []): void {
    options.forEach((option) => {
      if (!option.hasOwnProperty('label')) {
        option.label = option.value;
      }
    });

    this.options = options;
  }

  /**
   * Sets the visibility of the question, ensuring that all properties are initialized.
   * If a property is not provided, it will be initialized with default values.
   *
   * @param visibility
   */
  private setVisibility(visibility) {
    if (!visibility) {
      visibility = {};
    }

    visibility.externalConditions ??= {
      conditions: [],
      logicalOperator: LogicalOperators.And,
    };

    visibility.triggerQuestions ??= {
      questions: [],
      logicalOperator: LogicalOperators.And,
    };

    visibility.logicalOperator ??= LogicalOperators.And;

    this.visibility = visibility;
  }

  /**
   * Replaces placeholders in the given text with values from the provided external data.
   *
   * Placeholders are enclosed in double curly braces (e.g., {{placeholder}}).
   *
   * @param {string} text the string containing placeholders to be replaced
   * @param {ExternalData} values an object containing the values to replace the placeholders
   *
   * @returns {string} the string with placeholders replaced by their corresponding values, or unchanged placeholders
   * if no match is found
   */
  private replacePlaceholders(text: string, values: ExternalData): string {
    return text.replace(/{{([a-zA-Z0-9_]+)}}/g, (_, key) => {
      const value = values[key.trim()];

      return typeof value === 'string' ? value : `{{${key}}}`;
    });
  }

  /**
   * Processes an array of QuestionWarning, replacing placeholders in each warning message.
   *
   * @param {QuestionWarning[]} warnings the warnings array
   * @param {ExternalData} values the values to replace the placeholders
   *
   * @returns {QuestionWarning[]} the processed warnings with placeholders replaced
   */
  private processWarnings(warnings: QuestionWarning[], values: ExternalData): QuestionWarning[] {
    return warnings.map((warning) => ({
      ...warning,
      message: this.replacePlaceholders(warning.message, values),
    }));
  }

  /**
   * Processes a QuestionHint, replacing placeholders in the hint message.
   *
   * @param {QuestionHint} hint the hint to process
   * @param {ExternalData} values the values to replace the placeholders
   *
   * @returns {QuestionHint} the processed hint with placeholders replaced
   */
  private processHint(hint: QuestionHint, values: ExternalData): QuestionHint {
    return {
      ...hint,
      message: this.replacePlaceholders(hint.message, values),
    };
  }
}
